import { isBefore, parseISO } from 'date-fns';

/**
 * Date-based feature access configuration
 */
export const FEATURE_ACCESS_DATES = {
  'promotional-offers': {
    cutoffDate: '2025-08-22', // Users created before this date get access
    description: 'Available to users who created ads before August 22, 2025'
  }
} as const;

/**
 * Check if a user has access to a feature based on their creation date
 * @param featureId - The feature ID to check
 * @param userCreatedAt - The user's creation date
 * @returns boolean indicating if the user has access
 */
export function hasDateBasedFeatureAccess(
  featureId: keyof typeof FEATURE_ACCESS_DATES,
  userCreatedAt: Date | string | any
): boolean {
  const featureConfig = FEATURE_ACCESS_DATES[featureId];
  if (!featureConfig) {
    return false; // Feature not configured for date-based access
  }

  try {
    let userDate: Date;
    
    // Handle different date formats from Firestore
    if (userCreatedAt && typeof userCreatedAt.toDate === 'function') {
      // Firestore Timestamp
      userDate = userCreatedAt.toDate();
    } else if (userCreatedAt && typeof userCreatedAt === 'object' && userCreatedAt.seconds) {
      // Firestore Timestamp with seconds
      userDate = new Date(userCreatedAt.seconds * 1000);
    } else if (userCreatedAt && typeof userCreatedAt === 'object' && userCreatedAt._seconds) {
      // Firestore Timestamp with _seconds
      userDate = new Date(userCreatedAt._seconds * 1000);
    } else if (userCreatedAt instanceof Date) {
      // Native Date object
      userDate = userCreatedAt;
    } else if (typeof userCreatedAt === 'string') {
      // ISO string
      userDate = new Date(userCreatedAt);
    } else if (typeof userCreatedAt === 'number') {
      // Unix timestamp
      if (userCreatedAt.toString().length === 10) {
        userDate = new Date(userCreatedAt * 1000); // Seconds to milliseconds
      } else {
        userDate = new Date(userCreatedAt); // Already in milliseconds
      }
    } else {
      console.warn('Invalid user creation date format:', userCreatedAt);
      return false;
    }

    // Parse the cutoff date
    const cutoffDate = parseISO(featureConfig.cutoffDate);
    
    // User has access if they were created before the cutoff date
    return isBefore(userDate, cutoffDate);
  } catch (error) {
    console.error('Error checking date-based feature access:', error);
    return false;
  }
}

/**
 * Check if a user has access to promotional offers based on creation date
 * @param userCreatedAt - The user's creation date
 * @returns boolean indicating if the user has access
 */
export function hasPromotionalOffersAccess(userCreatedAt: Date | string | any): boolean {
  const result = hasDateBasedFeatureAccess('promotional-offers', userCreatedAt);
  return result;
}
